
import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import { useContentImages } from '@/hooks/useContentImages';
import { ArrowRight } from 'lucide-react';
import ProjectModal from '../components/home/ProjectModal';
import projectsData from '@/data/projects.json';

interface Project {
  id: string
  title: string
  description: string
  category: string
  tech_stack: string[]
  image_url: string | null
  project_url: string | null
  is_featured: boolean
  display_order: number
  is_active: boolean
  problem?: string | null
  solution?: string | null
  impact?: string | null
}

const Projects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { images: projectImages } = useContentImages('projects');
  
  useEffect(() => {
    // Load static data
    const data = (projectsData as Project[])
      .filter(p => p.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
    setProjects(data)
    setLoading(false)
  }, []);

  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const categories = ['All', ...Array.from(new Set(projects.map(project => project.category)))];
  
  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(project => project.category === filter);

  const featuredProjects = projects.filter(project => project.is_featured);
  const heroImage = projectImages[0];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div>Loading projects...</div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout>
        {/* Hero Section */}
          <section className="relative flex items-center justify-center overflow-hidden">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-800/70 to-blue-900/80 z-10"></div>
              <img
                src={heroImage?.image_url}
                alt={heroImage?.alt_text || ""}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Background Pattern */}
            <div className="absolute inset-0 z-20 opacity-10">
              <div className="absolute top-20 left-20 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
              <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500 rounded-full blur-3xl"></div>
            </div>

            <div className="container-custom relative z-30 text-center pt-24 pb-24">
              <div className="max-w-4xl mx-auto animate-on-scroll">
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Our Projects</h1>
                  <p className="text-xl text-slate-200">
                    Explore our portfolio of successful projects delivered across various industries.
                  </p>
              </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
              <div className="w-6 h-10 border-2 border-white/60 rounded-full flex justify-center">
                <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-pulse"></div>
              </div>
            </div>
          </section>
        
        {/* Featured Projects */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <h2 className="text-3xl font-bold mb-12 text-center animate-on-scroll">Featured Projects</h2>
            
            {featuredProjects.length === 0 ? (
              <div className="text-center text-gray-500">
                <p>No featured projects found. Please run the data fetch script to populate content.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                {featuredProjects.slice(0, 2).map((project, index) => (
                  <div 
                    key={project.id}
                    className="bg-white rounded-lg overflow-hidden shadow-lg animate-on-scroll card-hover group cursor-pointer"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="h-64 md:h-80 overflow-hidden relative">
                      <img 
                        src={project.image_url || "/placeholder.svg"} 
                        alt={project.title} 
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                        {/* Corner Lines */}
                        <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100"></div>
                        <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100"></div>
                        
                        {/* Text Overlay */}
                        <div className="text-center text-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                          <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                          <p className="text-sm">
                            {project.category} x {project.tech_stack[0] || 'Technology'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-8">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-sm font-medium text-goeltec-blue bg-goeltec-lightblue px-3 py-1 rounded-full">
                          {project.category}
                        </span>
                      </div>
                      <h3 className="text-2xl font-semibold mb-3">{project.title}</h3>
                      <p className="text-gray-600 mb-6">{project.description}</p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {project.tech_stack.map((tech) => (
                          <span 
                            key={tech} 
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      <div className="text-goeltec-blue font-medium inline-flex items-center hover:underline">
                        View Case Study
                        <ArrowRight size={16} className="ml-1" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        
        {/* All Projects */}
        <section className="py-16 bg-goeltec-background ">
          <div className="container-custom">
            <h2 className="text-3xl font-bold mb-12 text-center animate-on-scroll">All Projects</h2>
            
            {/* Filter Buttons */}
            {categories.length > 1 && (
              <div className="flex flex-wrap justify-center gap-3 mb-12 animate-on-scroll">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`px-4 py-2 rounded-full transition-colors ${
                      filter === category 
                        ? 'bg-goeltec-blue text-white' 
                        : 'bg-white text-goeltec-text hover:bg-goeltec-lightblue'
                    }`}
                    onClick={() => setFilter(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
            )}
            
            {/* Projects Grid */}
            {filteredProjects.length === 0 ? (
              <div className="text-center text-gray-500">
                <p>No projects found. Please run the data fetch script to populate content.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project, index) => (
                  <div 
                    key={project.id}
                    className="bg-white rounded-lg overflow-hidden shadow-md animate-on-scroll card-hover group cursor-pointer"
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="h-48 overflow-hidden relative">
                      <img 
                        src={project.image_url || "/placeholder.svg"} 
                        alt={project.title} 
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                        {/* Corner Lines */}
                        <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100"></div>
                        <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100"></div>
                        
                        {/* Text Overlay */}
                        <div className="text-center text-white opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                          <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                          <p className="text-sm">
                            {project.category} x {project.tech_stack[0] || 'Technology'}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-goeltec-blue bg-goeltec-lightblue px-3 py-1 rounded-full">
                          {project.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{project.title}</h3>
                      <p className="text-gray-600 mb-3">{project.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.tech_stack.map((tech) => (
                          <span 
                            key={tech} 
                            className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      <div className="text-goeltec-blue font-medium inline-flex items-center hover:underline">
                        View Details
                        <ArrowRight size={16} className="ml-1" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </Layout>
      
      {/* Project Modal */}
      <ProjectModal 
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default Projects;
